import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import type { ISignalProviderListCard } from '@brushfire/types'
import {
  DashboardStatusFilter,
  type DashboardStatusFilterOption,
} from '@/features/dashboard/components/DashboardStatusFilter'
import { ErrorState } from '@/shared/components/ErrorState'
import { ListPageCountBadge } from '@/shared/components/ListPageCountBadge'
import { SensemakerPageTitle } from '@/shared/components/SensemakerPageTitle'
import { StandardPageTitleRow } from '@/shared/components/StandardPageTitleRow'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast'
import { useSignalProviderListFiltersStore } from '@/stores/useSignalProviderListFiltersStore'
import {
  useDeactivateSignalProvider,
  useReactivateSignalProvider,
} from '../api/signal-provider-list-mutations'
import { useSignalProviderList } from '../api/signal-provider-list-queries'
import {
  SignalProviderDeactivateModal,
  SignalProviderDetailModal,
  SignalProviderFiltersPopover,
  SignalProviderListEmptyState,
  SignalProviderListPageSkeleton,
  SignalProviderListTable,
  SignalProviderQuickStatPills,
  SignalProviderReactivateModal,
} from '../components'
import { filterSignalProviderCards } from '../lib/signal-provider-list-filters'
import type { SignalProviderVerifiedFilter } from '../lib/signal-provider-list-filters'
import {
  SP_LIST_DEFAULT_ROWS_PER_PAGE,
  SP_LIST_FILTER_GAP_PX,
  SP_LIST_FILTER_WIDTH_PX,
} from '../lib/signal-provider-list-layout'
import { useSignalProviderListSearch } from '../lib/signal-provider-list-search'
import {
  DEFAULT_SIGNAL_PROVIDER_SORT,
  sortSignalProviderCards,
  type SignalProviderSortState,
} from '../lib/signal-provider-list-sort'

const VERIFIED_FILTER_OPTIONS: readonly DashboardStatusFilterOption<SignalProviderVerifiedFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'failed', label: 'Failed' },
]

function displayName(card: ISignalProviderListCard): string {
  return `${card.firstName} ${card.lastName}`.trim()
}

export function SignalProviderListPage() {
  useDocumentTitle('Signal Provider List')
  const { showToast } = useToast()
  const { search, setSearch } = useSignalProviderListSearch()
  const { data, isLoading, isError, refetch } = useSignalProviderList()
  const deactivateMutation = useDeactivateSignalProvider()
  const reactivateMutation = useReactivateSignalProvider()

  const demographicFilters = useSignalProviderListFiltersStore((s) => s.demographicFilters)
  const verifiedFilter = useSignalProviderListFiltersStore((s) => s.verifiedFilter)
  const setDemographicFilters = useSignalProviderListFiltersStore((s) => s.setDemographicFilters)
  const setVerifiedFilter = useSignalProviderListFiltersStore((s) => s.setVerifiedFilter)
  const clearDemographicFilters = useSignalProviderListFiltersStore((s) => s.clearDemographicFilters)

  const [sort, setSort] = useState<SignalProviderSortState>(DEFAULT_SIGNAL_PROVIDER_SORT)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(SP_LIST_DEFAULT_ROWS_PER_PAGE)
  const [detailUserId, setDetailUserId] = useState<string | null>(null)
  const [pendingCard, setPendingCard] = useState<ISignalProviderListCard | null>(null)
  const [modalMode, setModalMode] = useState<'deactivate' | 'reactivate' | null>(null)

  useEffect(() => {
    setPage(0)
  }, [search, verifiedFilter, demographicFilters])

  const filteredItems = useMemo(
    () =>
      sortSignalProviderCards(
        filterSignalProviderCards(
          data?.items ?? [],
          search,
          verifiedFilter,
          demographicFilters,
        ),
        sort,
      ),
    [data?.items, search, verifiedFilter, demographicFilters, sort],
  )

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage
    return filteredItems.slice(start, start + rowsPerPage)
  }, [filteredItems, page, rowsPerPage])

  const summary = data?.summary ?? { total: 0, verified: 0, unverified: 0, failed: 0 }

  const clearAllFilters = () => {
    clearDemographicFilters()
    setVerifiedFilter('all')
    setSearch('')
    setPage(0)
  }

  const closeModal = () => {
    if (deactivateMutation.isPending || reactivateMutation.isPending) return
    setModalMode(null)
    setPendingCard(null)
  }

  const confirmStatusChange = async () => {
    if (!pendingCard || !modalMode) return
    try {
      if (modalMode === 'deactivate') {
        await deactivateMutation.mutateAsync(pendingCard.platformUserId)
        showToast(`${displayName(pendingCard)} deactivated`, 'success')
      } else {
        await reactivateMutation.mutateAsync(pendingCard.platformUserId)
        showToast(`${displayName(pendingCard)} reactivated`, 'success')
      }
      closeModal()
    } catch {
      showToast('Unable to update signal provider status. Please try again.', 'error')
    }
  }

  if (isLoading) {
    return <SignalProviderListPageSkeleton />
  }

  if (isError || !data) {
    return <ErrorState onRetry={() => void refetch()} />
  }

  return (
    <Box data-testid="signal-provider-list-page">
      <StandardPageTitleRow
        actions={
          <Box
            sx={{
              display: 'flex',
              gap: `${SP_LIST_FILTER_GAP_PX}px`,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <SignalProviderFiltersPopover
              filters={demographicFilters}
              onChange={setDemographicFilters}
              onClearAll={clearDemographicFilters}
            />
            <DashboardStatusFilter
              value={verifiedFilter}
              onChange={setVerifiedFilter}
              options={VERIFIED_FILTER_OPTIONS}
              filterWidth={SP_LIST_FILTER_WIDTH_PX}
              labelPrefix="Filter by Verified Status"
              triggerDisplay="label-only"
              chevronSizePx={16}
              borderActive={verifiedFilter !== 'all'}
              testId="signal-provider-verified-status-filter"
            />
          </Box>
        }
      >
        <SensemakerPageTitle
          title="Signal Provider List"
          description="View and manage all registered Signal Providers."
          badge={
            <ListPageCountBadge testId="signal-provider-list-total-badge">
              {summary.total.toLocaleString()} Signal Providers
            </ListPageCountBadge>
          }
        />
      </StandardPageTitleRow>

      <SignalProviderQuickStatPills
        summary={summary}
        verifiedFilter={verifiedFilter}
        onVerifiedFilterChange={setVerifiedFilter}
      />

      {filteredItems.length === 0 ? (
        <SignalProviderListEmptyState onClearFilters={clearAllFilters} />
      ) : (
        <SignalProviderListTable
          rows={paginatedItems}
          totalFiltered={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          sort={sort}
          onSortChange={setSort}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onViewDetails={(card) => setDetailUserId(card.platformUserId)}
          onDeactivate={(card) => {
            setPendingCard(card)
            setModalMode('deactivate')
          }}
          onReactivate={(card) => {
            setPendingCard(card)
            setModalMode('reactivate')
          }}
        />
      )}

      <SignalProviderDetailModal
        platformUserId={detailUserId}
        open={detailUserId !== null}
        onClose={() => setDetailUserId(null)}
      />

      <SignalProviderDeactivateModal
        open={modalMode === 'deactivate' && pendingCard !== null}
        displayName={pendingCard ? displayName(pendingCard) : ''}
        pending={deactivateMutation.isPending}
        onCancel={closeModal}
        onConfirm={() => void confirmStatusChange()}
      />
      <SignalProviderReactivateModal
        open={modalMode === 'reactivate' && pendingCard !== null}
        displayName={pendingCard ? displayName(pendingCard) : ''}
        pending={reactivateMutation.isPending}
        onCancel={closeModal}
        onConfirm={() => void confirmStatusChange()}
      />
    </Box>
  )
}
